/**
 * Mobile helpers for the email arche: a viewport-width hook and swipe-gesture
 * preferences (configured in Email settings).
 *
 * Swipe prefs are now a HOLDER-WIDE server setting (see `useEmailUserSettings`)
 * — they follow the user across accounts and devices instead of living in
 * per-device localStorage. `useSwipePrefs` is a thin adapter keeping the old
 * `[prefs, update]` shape so consumers are unchanged.
 */

import { useState, useEffect } from 'react';
import { useEmailUserSettings, useSetEmailUserSettings } from './useEmailUserSettings';

/** True when the viewport is narrower than `breakpoint` (default 768px / md). */
export function useIsMobile(breakpoint = 768): boolean {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', onResize);
    onResize();
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

export type SwipeAction = 'none' | 'trash' | 'archive' | 'read' | 'star';

export interface SwipePrefs {
  enabled: boolean;
  /** Action when swiping a message row left (default: trash). */
  left: SwipeAction;
  /** Action when swiping a message row right (default: archive). */
  right: SwipeAction;
}

export const SWIPE_ACTION_LABELS: Record<SwipeAction, string> = {
  none: 'Nothing',
  trash: 'Trash',
  archive: 'Archive',
  read: 'Toggle read',
  star: 'Star',
};

export const DEFAULT_SWIPE_PREFS: SwipePrefs = { enabled: true, left: 'trash', right: 'archive' };

/**
 * Read + update swipe-gesture prefs. Backed by the holder-wide settings store,
 * so a change applies across every account and device.
 */
export function useSwipePrefs(): [SwipePrefs, (next: Partial<SwipePrefs>) => void] {
  const settings = useEmailUserSettings();
  const setAll = useSetEmailUserSettings();
  const update = (next: Partial<SwipePrefs>) => {
    setAll.mutate({ ...settings, swipe: { ...settings.swipe, ...next } });
  };
  return [settings.swipe, update];
}
